import { useIsomorphicLayoutEffect } from '@contra/react-hooks/useIsomorphicLayoutEffect';
import {
  type FloatingContext,
  useDismiss,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react-dom-interactions';
import { useRef, useState } from 'react';

export const useDialogSelect = ({
  context,
  onNavigate,
}: {
  context: FloatingContext;
  onNavigate?: () => void;
}) => {
  const listRef = useRef<Array<HTMLElement | null>>([]);
  const [activeIndex, setActiveIndex] = useState<null | number>(null);
  const { getFloatingProps, getItemProps, getReferenceProps } = useInteractions(
    [
      useRole(context, { role: 'listbox' }),
      useDismiss(context, { escapeKey: true, outsidePress: false }),
      useListNavigation(context, {
        activeIndex,
        listRef,
        loop: true,
        onNavigate: (index) => {
          onNavigate?.();
          setActiveIndex(index);
        },
        orientation: 'vertical',
        virtual: true,
      }),
    ],
  );

  useIsomorphicLayoutEffect(() => {
    // IMPORTANT: When the floating element first opens, this runs when the
    // styles have **not yet** been applied to the element. This can cause an
    // infinite loop as `size` has not yet limited the maxHeight, so the whole
    // page tries to scroll. We must wrap it in rAF.
    requestAnimationFrame(() => {
      if (activeIndex !== null) {
        const scrollIntoView = listRef.current[
          activeIndex
        ]?.scrollIntoView.bind(listRef.current[activeIndex]);

        if (scrollIntoView) {
          scrollIntoView({ block: 'nearest' });
        }
      }
    });
  }, [activeIndex]);

  return {
    activeIndex,
    getFloatingProps,
    getItemProps,
    getReferenceProps,
    listRef,
  };
};
